import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError } from '../api/client'

interface QueryState<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
}

/**
 * Загрузка данных с защитой от гонки: если ответ пришёл после того, как
 * параметры сменились или компонент размонтирован, он игнорируется.
 */
export function useQuery<T>(loader: () => Promise<T>, deps: unknown[] = []): QueryState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const generation = useRef(0)

  useEffect(() => {
    const current = ++generation.current
    setLoading(true)
    loader()
      .then((result) => {
        if (generation.current !== current) return
        setData(result)
        setError(null)
      })
      .catch((err: unknown) => {
        if (generation.current !== current) return
        setError(err instanceof ApiError ? err.message : String(err))
      })
      .finally(() => {
        if (generation.current === current) setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { data, error, loading, reload }
}

/** Действие с отслеживанием состояния: спиннер на кнопке и текст ошибки. */
export function useAction() {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(async (fn: () => Promise<unknown>): Promise<boolean> => {
    setPending(true)
    setError(null)
    try {
      await fn()
      return true
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : String(err))
      return false
    } finally {
      setPending(false)
    }
  }, [])

  return { run, pending, error, setError }
}
